// Global
import React, { useCallback, useRef, JSX, useId } from 'react';

// Local
import { SvgIcon } from 'components/helpers/SvgIcon';
import { useScrollElementIntoView } from 'lib/hooks/useScrollElementIntoView';
import { useAccordionItemContext, useAccordionContext } from './AccordionContext';
import { getTestProps } from 'lib/testing/utils';
import { AccordionItemData } from './AccordionContext';
import styles from './AccordionItem.module.scss';
import clsx from 'clsx';

export const AccordionItem = (props: AccordionItemData): JSX.Element => {
  const { uid, heading, content, icon } = props;

  const accordionId = useId();
  const { itemClassName, contentClassName } = useAccordionContext();
  const accordionButtonId = `${accordionId}-button`;
  const accordionContentId = `${accordionId}-content`;
  /*
   * REFS
   */

  const accordionItemButtonRef = useRef<HTMLButtonElement>(null);

  const accordionItemBodyRef = useRef<HTMLDivElement>(null);

  /*
   * State
   */

  const { isOpen, toggleOpen, scrollToOpenPanel } = useAccordionItemContext(uid ?? '');

  /*
   * Convenience Methods
   */

  const toggleAccordion = useCallback(() => {
    toggleOpen();
  }, [toggleOpen]);

  /*
   * Lifecycle
   */

  const scrollCondition = scrollToOpenPanel && isOpen;

  useScrollElementIntoView(accordionItemButtonRef.current, {
    stickyHeaderId: 'header',
    condition: scrollCondition,
    animationElement: accordionItemBodyRef.current,
    scrollTargetId: accordionId,
  });

  /*
   * Rendering
   */

  return (
    <div
      className={clsx(styles.accordionItem, itemClassName, {
        [styles['accordionItem--open']]: isOpen,
      })}
      data-component="authorable/shared/lists/accordionitem"
      key={accordionId}
      id={accordionId}
      {...getTestProps(`accordion-item-accordion-${accordionId}`)}
    >
      <h3>
        <button
          id={accordionButtonId}
          aria-controls={accordionContentId}
          aria-expanded={isOpen}
          className={styles.accordionItemToggle}
          onClick={toggleAccordion}
          ref={accordionItemButtonRef}
          type="button"
          {...getTestProps(`toggle-button`)}
        >
          <div className={styles.leftContainer}>
            {icon && <span className={clsx(styles.headingIcon)}>{icon}</span>}
            <span className={clsx(styles.toggleHeading)}>{heading}</span>
          </div>

          <SvgIcon
            className={clsx(styles.toggleIcon, { [styles['toggleIcon--open']]: isOpen })}
            fill="currentColor"
            icon={isOpen ? 'iconAccordionMinus' : 'iconAccordionPlus'}
            viewBox="0 0 40 40"
          />
        </button>
      </h3>
      <div
        id={accordionContentId}
        aria-labelledby={accordionButtonId}
        className={clsx(styles.contentContainer, contentClassName, {
          [styles['contentContainer--open']]: isOpen,
          [styles['contentContainer--closed']]: !isOpen,
        })}
        role="region"
        ref={accordionItemBodyRef}
        tabIndex={isOpen ? undefined : -1}
        aria-hidden={!isOpen}
      >
        <div tabIndex={-1}>{content}</div>
      </div>
    </div>
  );
};
